<script setup lang="ts">
import { computed } from 'vue'

import type { Stat } from '@collector/sportsapi-types'

import type { SportsStatConfig } from '../../../interfaces/SportStatConfig'
import {
  getStatisticDecimalPlaces,
  getStatisticInputDataType,
  isPercentFormat,
} from '../../../interfaces/SportStatConfig/utils'
import StatisticInput from '../../common/Inputs/StatisticInput.vue'
import { useParticipantStatUpdate } from './useParticipantStatUpdate'

type Props = {
  config: SportsStatConfig
  homeDirty?: boolean
  awayDirty?: boolean
}
const props = defineProps<Props>()

// state
const homeStat = defineModel<Stat>('homeStat', { required: true })
const awayStat = defineModel<Stat>('awayStat', { required: true })

// composables
const {
  isEventStatUpdated: isEventHomeStatUpdated,
  updateStat: updateHomeStat,
} = useParticipantStatUpdate(homeStat)

const {
  isEventStatUpdated: isEventAwayStatUpdated,
  updateStat: updateAwayStat,
} = useParticipantStatUpdate(awayStat)

// computed
const dirty = computed(
  () =>
    props.homeDirty ||
    props.awayDirty ||
    isEventHomeStatUpdated.value ||
    isEventAwayStatUpdated.value,
)
const inputDataType = computed(() =>
  getStatisticInputDataType(props.config.sportStat, props.config.format),
)
const decimalPlaces = computed(() =>
  getStatisticDecimalPlaces(props.config.format),
)
const percent = computed(
  () => props.config.format && isPercentFormat(props.config.format),
)
const disabled = computed(() => props.config.edit?.disabled)
</script>

<template>
  <div
    class="hover:bg-neutral-light-5 flex items-stretch gap-2 rounded-md p-1 transition-colors"
  >
    <StatisticInput
      :modelValue="homeStat"
      :dirty="homeDirty"
      :dataType="inputDataType"
      :decimalPlaces
      :disabled
      :formatPercent="percent"
      @update:modelValue="updateHomeStat"
    />
    <div
      class="border-neutral-light-6 flex w-full items-center justify-center truncate border-b px-2 text-sm"
      :class="{ 'border-neutral-light-0 !border-b-2 font-bold': dirty }"
    >
      {{ config.name || config.sportStat.name }}
      {{ percent && !disabled ? '(%)' : '' }}
    </div>
    <StatisticInput
      :modelValue="awayStat"
      :dirty="awayDirty"
      :dataType="inputDataType"
      :decimalPlaces
      :disabled
      :formatPercent="percent"
      @update:modelValue="updateAwayStat"
    />
  </div>
</template>
